import axios from "axios";
import { HOST_URL } from "./actions/Host";
import { removeAuthedUser, updateAuthedUser } from "./actions/AuthUserActions";

// Renews the session when the access token has expired:
// - the refused calls wait for one single renewal (POST /auth/refresh),
// - they are then sent again with the new access token,
// - if the session cannot be renewed, the user is signed out.

// Bare client for the renewal, so that it is never intercepted itself
const refreshClient = axios.create();

// Renewal in progress, shared by every call refused meanwhile
let renewal = null;

// Access tokens already replaced: calls prepared with one of them (by a page
// still holding it) are sent with the current token instead
const replacedTokens = new Set();

const bearerOf = (config) => {
  const header = config?.headers?.Authorization;

  return typeof header === "string" && header.startsWith("Bearer ")
    ? header.slice(7)
    : null;
};

const isApiCall = (config) =>
  typeof config?.url === "string" && config.url.startsWith(HOST_URL);

function endSession(store) {
  store.dispatch(removeAuthedUser());

  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign("/login?session=expired");
  }
}

function renewSession(store) {
  if (!renewal) {
    renewal = (async () => {
      const { user } = store.getState().authUser;

      if (!user?.refreshToken) {
        endSession(store);
        throw new Error("No refresh token");
      }

      try {
        const { data } = await refreshClient.post(`${HOST_URL}/auth/refresh`, {
          refreshToken: user.refreshToken,
        });
        const { accessToken, refreshToken } = data.data;

        if (user.token) replacedTokens.add(user.token);
        store.dispatch(updateAuthedUser({ token: accessToken, refreshToken }));

        return accessToken;
      } catch (error) {
        // Without an answer (network down), the session may still be valid:
        // the user stays signed in and can try again
        if (error.response) endSession(store);

        throw error;
      }
    })().finally(() => {
      renewal = null;
    });
  }

  return renewal;
}

export function setupAuthInterceptors(store) {
  axios.interceptors.request.use((config) => {
    const token = bearerOf(config);
    const current = store.getState().authUser?.user?.token;

    if (token && current && replacedTokens.has(token)) {
      config.headers.Authorization = `Bearer ${current}`;
    }

    return config;
  });

  axios.interceptors.response.use(undefined, async (error) => {
    const { config, response } = error;
    const token = bearerOf(config);

    // Only signed-in calls to our API refused for an expired session, once
    if (
      response?.status !== 401 ||
      !token ||
      !isApiCall(config) ||
      config._sessionRenewed
    ) {
      throw error;
    }

    const current = store.getState().authUser?.user?.token;

    // A renewal already happened since this call was prepared
    const freshToken =
      current && current !== token ? current : await renewSession(store);

    config._sessionRenewed = true;
    config.headers.Authorization = `Bearer ${freshToken}`;

    return axios(config);
  });
}
